import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { getAllEmployees } from '../lib/employees.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { isValidPinCode, normalizePinCodeInput } from '../lib/pinCode.js';

interface CredentialRow {
  id: string;
  tenant_id: string;
  employee_id: string;
  type: 'nfc_card' | 'mobile_key' | 'pin_code';
  status: 'pending' | 'active' | 'revoked';
  sync_status: 'pending' | 'synced' | 'failed' | 'retry';
  label: string | null;
  unifi_credential_id: string | null;
  last_sync_error: string | null;
  created_at: string;
  tenant: { id: string; name: string; status: string };
  employee: { id: string; name: string; email: string; status: string };
}

interface TenantSummary {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

interface EmployeeOption {
  id: string;
  name: string;
  email: string;
  status: string;
  sync_status: string;
  unifi_user_id: string | null;
}

interface TouchPassRow {
  id: string;
  card_id: string | null;
  card_name: string | null;
  status: string;
  user_id: string | null;
}

interface CredentialPolicy {
  touch_credential_enabled: boolean;
  third_party_nfc_enabled: boolean;
  touch_pass_management_url: string | null;
}

interface CostInventoryRow {
  id: string;
  external_id: string;
  display_name: string | null;
  card_id: string | null;
  card_name: string | null;
  source_status: string | null;
  status: 'unallocated' | 'allocated' | 'assigned' | 'lost' | 'retired';
  tenant_id: string | null;
  tenant: { id: string; name: string; status: string } | null;
  credential_id: string | null;
  assigned_employee_id: string | null;
  assigned_employee: { id: string; name: string; email: string; status: string } | null;
  allocated_at: string | null;
  assigned_at: string | null;
  last_synced_at: string | null;
}

type ModalState =
  | { kind: 'issue' }
  | { kind: 'revoke'; row: CredentialRow }
  | { kind: 'allocate'; row: CostInventoryRow }
  | { kind: 'inventory-status'; row: CostInventoryRow };

const credentialTypeLabel: Record<CredentialRow['type'], string> = {
  nfc_card: 'NFC card',
  mobile_key: 'Phone KeyPass',
  pin_code: 'PIN',
};

const AUTO_TOUCH_PASS_SYNC_INTERVAL_MS = 30_000;
const AUTO_TOUCH_PASS_SYNC_COOLDOWN_MS = 10_000;

async function fetchCredentialStore() {
  const [credentials, costInventory, tenants, policy] = await Promise.all([
    apiClient.get<{ data: CredentialRow[] }>('/api/v1/admin/credentials/inventory', {
      skipTenantScope: true,
    }),
    apiClient.get<{ data: CostInventoryRow[] }>('/api/v1/admin/credentials/cost-inventory', {
      skipTenantScope: true,
    }),
    apiClient
      .get<{ data: TenantSummary[] }>('/api/v1/tenants', { skipTenantScope: true })
      .catch(() => ({ data: [] as TenantSummary[] })),
    apiClient
      .get<{ data: CredentialPolicy }>('/api/v1/admin/credentials/policy', {
        skipTenantScope: true,
      })
      .catch(() => ({ data: null as CredentialPolicy | null })),
  ]);
  return {
    credentials: credentials.data,
    costInventory: costInventory.data,
    tenants: tenants.data,
    touchCredentialEnabled: policy.data?.touch_credential_enabled ?? false,
    touchPassManagementUrl: policy.data?.touch_pass_management_url ?? null,
  };
}

export function AdminCredentialInventoryPage() {
  const [rows, setRows] = useState<CredentialRow[] | null>(null);
  const [costRows, setCostRows] = useState<CostInventoryRow[] | null>(null);
  const [tenantRows, setTenantRows] = useState<TenantSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [touchCredentialEnabled, setTouchCredentialEnabled] = useState(false);
  const [touchPassManagementUrl, setTouchPassManagementUrl] = useState<string | null>(null);
  const autoSyncInFlight = useRef(false);
  const lastAutoSyncAt = useRef(0);

  const applyCredentialStore = useCallback(
    (snapshot: Awaited<ReturnType<typeof fetchCredentialStore>>) => {
      setRows(snapshot.credentials);
      setCostRows(snapshot.costInventory);
      setTenantRows(snapshot.tenants);
      setTouchCredentialEnabled(snapshot.touchCredentialEnabled);
      setTouchPassManagementUrl(snapshot.touchPassManagementUrl);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setCostRows(null);
    setError(null);
    fetchCredentialStore()
      .then((snapshot) => {
        if (cancelled) return;
        applyCredentialStore(snapshot);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load credentials.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applyCredentialStore, reloadTick]);

  const tenants = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows ?? []) map.set(row.tenant.id, row.tenant.name);
    for (const tenant of tenantRows) map.set(tenant.id, tenant.name);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows, tenantRows]);

  const visibleRows = (rows ?? []).filter((row) => tenantFilter === 'all' || row.tenant_id === tenantFilter);
  const visibleCostRows = (costRows ?? []).filter(
    (row) => tenantFilter === 'all' || row.tenant_id === tenantFilter,
  );

  const syncTouchPassInventory = useCallback(
    async (showToast: boolean) => {
      const res = await apiClient.post<{ data: { synced: number; created: number } }>(
        '/api/v1/admin/credentials/cost-inventory/sync-touch-passes',
        {},
        { skipTenantScope: true },
      );
      applyCredentialStore(await fetchCredentialStore());
      if (showToast) {
        setToast(`Touch pass inventory synced. ${res.data.created} new, ${res.data.synced} seen.`);
      }
    },
    [applyCredentialStore],
  );

  useEffect(() => {
    let cancelled = false;

    const syncIfVisible = async () => {
      if (
        cancelled ||
        autoSyncInFlight.current ||
        document.visibilityState === 'hidden' ||
        Date.now() - lastAutoSyncAt.current < AUTO_TOUCH_PASS_SYNC_COOLDOWN_MS
      ) {
        return;
      }
      autoSyncInFlight.current = true;
      try {
        await syncTouchPassInventory(false);
      } catch {
        // Keep the last-known store visible; the manual Sync button still reports failures.
      } finally {
        lastAutoSyncAt.current = Date.now();
        autoSyncInFlight.current = false;
      }
    };

    void syncIfVisible();
    const syncInterval = window.setInterval(() => void syncIfVisible(), AUTO_TOUCH_PASS_SYNC_INTERVAL_MS);
    const onFocus = () => void syncIfVisible();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void syncIfVisible();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(syncInterval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [syncTouchPassInventory]);

  async function syncTouchPasses() {
    setSyncing(true);
    setError(null);
    try {
      await syncTouchPassInventory(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to sync touch pass inventory.');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="font-headline-md text-headline-md text-on-surface">Credential inventory</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Paid Touch Pass inventory, tenant allocation, issued credentials, and controller sync state.
          </p>
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end xl:justify-end">
          <label className="grid gap-1 min-w-56">
            <span className="font-label-md text-label-md text-on-surface-variant">Tenant</span>
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="rounded border border-outline bg-surface px-3 py-2"
            >
              <option value="all">All tenants</option>
              {tenants.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            {touchCredentialEnabled ? (
              <Button type="button" onClick={() => setModal({ kind: 'issue' })}>
                <Icon name="phone_iphone" fill />
                <span>Issue Phone KeyPass</span>
              </Button>
            ) : null}
            <Button type="button" variant="secondary" onClick={syncTouchPasses} loading={syncing}>
              <Icon name="sync" />
              <span>Sync Touch Passes</span>
            </Button>
            {touchPassManagementUrl ? (
              <a
                href={touchPassManagementUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-surface-container px-4 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
              >
                <Icon name="open_in_new" />
                <span>Manage in UniFi</span>
              </a>
            ) : null}
            <a
              href="/api/v1/admin/credentials/cost-inventory/report?format=csv"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-surface-container px-4 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
            >
              <Icon name="download" />
              <span>Export</span>
            </a>
            <Link
              to={`/credentials/enroll${tenantFilter !== 'all' ? `?tenantId=${encodeURIComponent(tenantFilter)}` : ''}`}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-surface-container px-4 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
            >
              <Icon name="contactless" />
              <span>Enroll credential</span>
            </Link>
          </div>
        </div>
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="underline font-medium">
            Dismiss
          </button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}

      {!rows && !error ? (
        <div className="p-12 flex justify-center">
          <Spinner />
        </div>
      ) : null}

      {costRows ? (
        <Card>
          <CardBody>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-headline-sm text-headline-sm text-on-surface">
                  Cost-bearing inventory
                </h2>
                <p className="text-on-surface-variant">
                  Touch Passes that cost the building owner money to hold.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                <InventoryCount label="Allocated" value={costRows.filter((row) => row.status === 'allocated').length} />
                <InventoryCount label="Assigned" value={costRows.filter((row) => row.status === 'assigned').length} />
                <InventoryCount label="Lost" value={costRows.filter((row) => row.status === 'lost').length} />
              </div>
            </div>
            {visibleCostRows.length === 0 ? (
              <p className="py-3 text-on-surface-variant">
                No cost-bearing credentials match this view.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-on-surface-variant">
                    <tr className="border-b border-outline-variant">
                      <th className="py-2 pr-4">Credential</th>
                      <th className="py-2 pr-4">Tenant</th>
                      <th className="py-2 pr-4">Assigned person</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Last sync</th>
                      <th className="py-2 pr-4 text-right">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant">
                    {visibleCostRows.map((row) => (
                      <tr key={row.id}>
                        <td className="py-3 pr-4">
                          <div className="font-medium text-on-surface">
                            {row.display_name ?? row.external_id}
                          </div>
                          <div className="text-on-surface-variant font-mono text-xs">
                            {row.external_id}
                          </div>
                        </td>
                        <td className="py-3 pr-4">{row.tenant?.name ?? 'Building reserve'}</td>
                        <td className="py-3 pr-4">
                          {row.assigned_employee ? (
                            <>
                              <div>{row.assigned_employee.name}</div>
                              <div className="text-on-surface-variant">{row.assigned_employee.email}</div>
                            </>
                          ) : (
                            <span className="text-on-surface-variant">Unassigned</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge tone={inventoryTone(row.status)}>{row.status}</Badge>
                        </td>
                        <td className="py-3 pr-4">
                          {row.last_synced_at ? new Date(row.last_synced_at).toLocaleString() : 'Not synced'}
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <div className="flex justify-end gap-3">
                            {row.status !== 'assigned' ? (
                              <button
                                type="button"
                                onClick={() => setModal({ kind: 'allocate', row })}
                                className="text-primary hover:underline font-label-md text-label-md"
                              >
                                Allocate
                              </button>
                            ) : null}
                            {row.status !== 'assigned' && row.tenant_id ? (
                              <button
                                type="button"
                                onClick={() => setModal({ kind: 'inventory-status', row })}
                                className="text-on-surface hover:underline font-label-md text-label-md"
                              >
                                Status
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}

      {rows ? (
        <Card>
          <CardBody>
            <div className="mb-4">
              <h2 className="font-headline-sm text-headline-sm text-on-surface">
                Issued credentials
              </h2>
              <p className="text-on-surface-variant">
                Person-bound credentials and controller sync state.
              </p>
            </div>
            {visibleRows.length === 0 ? (
              <p className="py-3 text-on-surface-variant">No credentials match this view.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-on-surface-variant">
                    <tr className="border-b border-outline-variant">
                      <th className="py-2 pr-4">Person</th>
                      <th className="py-2 pr-4">Tenant</th>
                      <th className="py-2 pr-4">Credential</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Sync</th>
                      <th className="py-2 pr-4">Controller ID</th>
                      <th className="py-2 pr-4 text-right">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant">
                    {visibleRows.map((row) => (
                      <tr key={row.id}>
                        <td className="py-3 pr-4">
                          <div className="font-medium text-on-surface">{row.employee.name}</div>
                          <div className="text-on-surface-variant">{row.employee.email}</div>
                        </td>
                        <td className="py-3 pr-4">{row.tenant.name}</td>
                        <td className="py-3 pr-4">
                          <div>{credentialTypeLabel[row.type]}</div>
                          <div className="text-on-surface-variant">{row.label ?? 'Unlabeled'}</div>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge tone={row.status === 'active' ? 'success' : row.status === 'pending' ? 'warning' : 'neutral'}>
                            {row.status}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge tone={row.sync_status === 'synced' ? 'success' : row.sync_status === 'failed' ? 'danger' : 'warning'}>
                            {row.sync_status}
                          </Badge>
                          {row.last_sync_error ? (
                            <div className="text-error mt-1">{row.last_sync_error}</div>
                          ) : null}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          {row.unifi_credential_id ?? 'not synced'}
                        </td>
                        <td className="py-3 pr-4 text-right">
                          {row.status !== 'revoked' ? (
                            <button
                              type="button"
                              onClick={() => setModal({ kind: 'revoke', row })}
                              className="text-error hover:underline font-label-md text-label-md"
                              aria-label={`Revoke ${row.label ?? credentialTypeLabel[row.type]}`}
                            >
                              Revoke
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}

      {modal?.kind === 'issue' ? (
        <IssueCredentialModal
          tenants={tenants}
          initialTenantId={tenantFilter === 'all' ? null : tenantFilter}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : modal?.kind === 'revoke' ? (
        <RevokeCredentialModal
          row={modal.row}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : modal?.kind === 'allocate' ? (
        <AllocateInventoryModal
          row={modal.row}
          tenants={tenantRows.filter((tenant) => tenant.status === 'active')}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : modal?.kind === 'inventory-status' ? (
        <InventoryStatusModal
          row={modal.row}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            setReloadTick((tick) => tick + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function inventoryTone(status: CostInventoryRow['status']): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'allocated' || status === 'assigned') return 'success';
  if (status === 'lost') return 'danger';
  if (status === 'retired') return 'neutral';
  return 'warning';
}

function InventoryCount({ label, value }: { label: string; value: number }) {
  return (
    <span className="rounded bg-surface-container px-3 py-1 text-on-surface">
      {label}: {value}
    </span>
  );
}

function tenantRequestInit(tenantId: string) {
  return { skipTenantScope: true, headers: { 'x-tenant-context': tenantId } };
}

function IssueCredentialModal({
  tenants,
  initialTenantId,
  onCancel,
  onDone,
}: {
  tenants: Array<[string, string]>;
  initialTenantId: string | null;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [tenantId, setTenantId] = useState(initialTenantId ?? tenants[0]?.[0] ?? '');
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [type, setType] = useState<CredentialRow['type']>('pin_code');
  const [label, setLabel] = useState('');
  const [cardToken, setCardToken] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [touchPassId, setTouchPassId] = useState('');
  const [touchPasses, setTouchPasses] = useState<TouchPassRow[]>([]);
  const [touchPassLoadError, setTouchPassLoadError] = useState<string | null>(null);
  // Distinct from touchPasses.length: the selected tenant can have every
  // allocated pass already in use (touchPasses empty) while still being
  // entitled to the Phone KeyPass option — this tracks entitlement, not
  // current availability, and gates whether the option even appears.
  const [tenantHasTouchCredentials, setTenantHasTouchCredentials] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setTenantHasTouchCredentials(false);
      return;
    }
    let cancelled = false;
    apiClient
      .get<{ data: TouchPassRow[]; meta?: { tenant_has_touch_credentials?: boolean } }>(
        '/api/v1/employees/credentials/touch-passes/assignable',
        tenantRequestInit(tenantId),
      )
      .then((res) => {
        if (cancelled) return;
        setTenantHasTouchCredentials(res.meta?.tenant_has_touch_credentials ?? res.data.length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setTenantHasTouchCredentials(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setEmployees([]);
      setEmployeeId('');
      return;
    }
    let cancelled = false;
    setLoadingEmployees(true);
    setError(null);
    getAllEmployees<EmployeeOption>(tenantRequestInit(tenantId), 200)
      .then((res) => {
        if (cancelled) return;
        setEmployees(res.data);
        setEmployeeId((current) => current || res.data[0]?.id || '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEmployees([]);
        setEmployeeId('');
        setError(err instanceof ApiError ? err.message : 'Failed to load employees.');
      })
      .finally(() => {
        if (!cancelled) setLoadingEmployees(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (type === 'mobile_key' && !tenantHasTouchCredentials) setType('pin_code');
  }, [type, tenantHasTouchCredentials]);

  useEffect(() => {
    if (!tenantId || type !== 'mobile_key') {
      setTouchPasses([]);
      setTouchPassId('');
      setTouchPassLoadError(null);
      return;
    }
    let cancelled = false;
    setTouchPassLoadError(null);
    apiClient
      .get<{ data: TouchPassRow[] }>(
        '/api/v1/employees/credentials/touch-passes/assignable',
        tenantRequestInit(tenantId),
      )
      .then((res) => {
        if (cancelled) return;
        setTouchPasses(res.data);
        setTouchPassId((current) => current || res.data[0]?.id || '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTouchPasses([]);
        setTouchPassId('');
        setTouchPassLoadError(
          err instanceof ApiError ? err.message : 'Could not load tenant Touch Passes.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, type]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!tenantId || !employeeId) {
      setError('Choose a tenant and employee.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const body: Record<string, unknown> = { type };
    if (label.trim()) body.label = label.trim();
    if (type === 'nfc_card') {
      if (!cardToken.trim()) {
        setSubmitting(false);
        setError('Card token is required for NFC cards.');
        return;
      }
      body.card_token = cardToken.trim();
    }
    if (type === 'mobile_key') {
      if (!touchPassId.trim()) {
        setSubmitting(false);
        setError('Choose a Phone KeyPass before issuing.');
        return;
      }
      body.touch_pass_id = touchPassId.trim();
    }
    if (type === 'pin_code') {
      if (!isValidPinCode(pinCode)) {
        setSubmitting(false);
        setError('Enter a 4 to 8 digit PIN code.');
        return;
      }
      body.pin_code = pinCode.trim();
    }
    try {
      await apiClient
        .withIdempotencyKey(key)
        .post(`/api/v1/employees/${employeeId}/credentials`, body, tenantRequestInit(tenantId));
      onDone('Credential issued.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Issue failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Issue Phone KeyPass" onCancel={onCancel}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Tenant" htmlFor="admin-credential-tenant">
          <select
            id="admin-credential-tenant"
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setEmployeeId('');
            }}
            className={fieldCls}
          >
            <option value="">Select tenant</option>
            {tenants.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Employee" htmlFor="admin-credential-employee">
          <select
            id="admin-credential-employee"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className={fieldCls}
            disabled={!tenantId || loadingEmployees}
          >
            <option value="">
              {loadingEmployees ? 'Loading employees...' : 'Select employee'}
            </option>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name} ({employee.email})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Type" htmlFor="admin-credential-type">
          <select
            id="admin-credential-type"
            value={type}
            onChange={(e) => setType(e.target.value as CredentialRow['type'])}
            className={fieldCls}
          >
            <option value="nfc_card">NFC card / fob</option>
            <option value="pin_code">PIN code</option>
            {tenantHasTouchCredentials ? <option value="mobile_key">Phone KeyPass</option> : null}
          </select>
        </Field>
        <Field label="Label" htmlFor="admin-credential-label">
          <input
            id="admin-credential-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={120}
            className={fieldCls}
          />
        </Field>
        {type === 'nfc_card' ? (
          <Field label="Card token" htmlFor="admin-credential-card-token">
            <input
              id="admin-credential-card-token"
              type="text"
              value={cardToken}
              onChange={(e) => setCardToken(e.target.value)}
              maxLength={120}
              className={`${fieldCls} font-code text-code`}
            />
          </Field>
        ) : null}
        {type === 'pin_code' ? (
          <Field label="PIN code" htmlFor="admin-credential-pin-code">
            <input
              id="admin-credential-pin-code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              pattern="[0-9]*"
              value={pinCode}
              onChange={(e) => setPinCode(normalizePinCodeInput(e.target.value))}
              maxLength={8}
              className={`${fieldCls} font-code text-code`}
            />
            <p className="mt-2 text-on-surface-variant font-body-sm text-body-sm">
              Use 4 to 8 digits. This is the code the person will type at a keypad.
            </p>
          </Field>
        ) : null}
        {type === 'mobile_key' ? (
          <Field label="Phone KeyPass" htmlFor="admin-credential-touch-pass">
            <select
              id="admin-credential-touch-pass"
              value={touchPassId}
              onChange={(e) => setTouchPassId(e.target.value)}
              className={fieldCls}
            >
              <option value="">Select a Phone KeyPass</option>
              {touchPasses.map((row) => (
                <option key={row.id} value={row.id}>
                  {(row.card_name && row.card_name.trim().length > 0 ? row.card_name : row.card_id) ?? row.id}
                </option>
              ))}
            </select>
            {touchPassLoadError ? (
              <p className="mt-2 text-error font-body-md text-body-md" role="alert">
                {touchPassLoadError}
              </p>
            ) : touchPasses.length === 0 ? (
              <p className="mt-2 text-on-surface-variant font-body-md text-body-md">
                No tenant-allocated Phone KeyPasses are available right now.
              </p>
            ) : null}
          </Field>
        ) : null}
        <input type="hidden" value={key} readOnly />
        {error ? <p role="alert" className="text-error">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting} disabled={type === 'pin_code' && !isValidPinCode(pinCode)}>
            Issue
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function RevokeCredentialModal({
  row,
  onCancel,
  onDone,
}: {
  row: CredentialRow;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key } = useIdempotencyKey();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient
        .withIdempotencyKey(key)
        .delete(
          `/api/v1/employees/${row.employee_id}/credentials/${row.id}`,
          tenantRequestInit(row.tenant_id),
        );
      onDone('Credential revoked.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Revoke failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Revoke credential?" onCancel={onCancel}>
      <div className="space-y-4">
        <p className="font-body-md text-body-md text-on-surface">
          Revoke {row.label ?? credentialTypeLabel[row.type]} for {row.employee.name} at{' '}
          {row.tenant.name}? This removes it from the controller and keeps the audit trail.
        </p>
        {error ? <p role="alert" className="text-error">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={revoke} loading={submitting}>
            Revoke
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function AllocateInventoryModal({
  row,
  tenants,
  onCancel,
  onDone,
}: {
  row: CostInventoryRow;
  tenants: TenantSummary[];
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const [tenantId, setTenantId] = useState(row.tenant_id ?? tenants[0]?.id ?? '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!tenantId) {
      setError('Choose a tenant.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(
        `/api/v1/admin/credentials/cost-inventory/${row.id}/allocate`,
        { tenant_id: tenantId, note: note.trim() || undefined },
        { skipTenantScope: true },
      );
      onDone('Credential allocated.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Allocation failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Allocate credential" onCancel={onCancel}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-on-surface">
          Assign tenant responsibility for {row.display_name ?? row.external_id}.
        </p>
        <Field label="Tenant" htmlFor="inventory-tenant">
          <select
            id="inventory-tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className={fieldCls}
          >
            <option value="">Select tenant</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Note" htmlFor="inventory-note">
          <input
            id="inventory-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            className={fieldCls}
          />
        </Field>
        {error ? <p role="alert" className="text-error">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Allocate
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function InventoryStatusModal({
  row,
  onCancel,
  onDone,
}: {
  row: CostInventoryRow;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const [action, setAction] = useState<'lost' | 'retired' | 'unallocated'>('lost');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (action === 'unallocated') {
        await apiClient.post(
          `/api/v1/admin/credentials/cost-inventory/${row.id}/reclaim`,
          { note: note.trim() || undefined },
          { skipTenantScope: true },
        );
        onDone('Credential reclaimed.');
        return;
      }
      await apiClient.post(
        `/api/v1/admin/credentials/cost-inventory/${row.id}/status`,
        { status: action, note: note.trim() || undefined },
        { skipTenantScope: true },
      );
      onDone('Credential status updated.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Status update failed.');
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Update inventory status" onCancel={onCancel}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-on-surface">
          Update {row.display_name ?? row.external_id} for {row.tenant?.name ?? 'building reserve'}.
        </p>
        <Field label="Action" htmlFor="inventory-status-action">
          <select
            id="inventory-status-action"
            value={action}
            onChange={(e) => setAction(e.target.value as typeof action)}
            className={fieldCls}
          >
            <option value="lost">Mark lost</option>
            <option value="retired">Mark retired</option>
            <option value="unallocated">Return to building reserve</option>
          </select>
        </Field>
        <Field label="Note" htmlFor="inventory-status-note">
          <input
            id="inventory-status-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            className={fieldCls}
          />
        </Field>
        {error ? <p role="alert" className="text-error">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Save
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({
  title,
  onCancel: _onCancel,
  children,
}: {
  title: string;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="credential-inventory-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="w-full max-w-[520px] bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8">
        <header className="flex items-center gap-3 mb-6">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary-container text-on-primary">
            <Icon name="key" />
          </div>
          <h2 id="credential-inventory-modal-title" className="font-headline-sm text-headline-sm text-on-surface">
            {title}
          </h2>
        </header>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="font-label-md text-label-md text-on-surface block">
        {label}
      </label>
      {children}
    </div>
  );
}

const fieldCls =
  'w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none';
